// The offer popup's button can lead to a page of this site, which opens in
// place, or to any other website, such as a Facebook event, which opens in a
// new tab. The API accepts a site path such as /coupons or a full http(s) link.
export type CtaTarget =
  | { isExternal: false; path: string }
  | { isExternal: true; url: string };

const SITE_PATH = /^\/(?!\/)\S*$/;
const URL_SCHEME = /^[a-z][a-z\d+.-]*:/i;

export const isValidCtaUrl = (ctaUrl: string): boolean => {
  if (SITE_PATH.test(ctaUrl)) {
    return true;
  }

  if (!/^https?:\/\/[^/\s]\S*$/i.test(ctaUrl)) {
    return false;
  }

  try {
    // Like the API, a link has to point at a real domain.
    return new URL(ctaUrl).hostname.includes('.');
  } catch (error) {
    return false;
  }
};

// Links are often pasted without their scheme, such as facebook.com/events/…,
// so such a link is completed with https:// before it is checked and saved.
export const normalizeCtaUrl = (value: string): string => {
  const ctaUrl = value.trim();

  if (!ctaUrl || SITE_PATH.test(ctaUrl) || URL_SCHEME.test(ctaUrl)) {
    return ctaUrl;
  }

  return `https://${ctaUrl}`;
};

// Without a usable link the button keeps leading to its default page. A link
// to this very site, such as https://citywave.lt/coupons, opens in place like
// a site path does.
export const getCtaTarget = (
  ctaUrl: string | null | undefined,
  defaultPath: string,
): CtaTarget => {
  if (!ctaUrl || !isValidCtaUrl(ctaUrl)) {
    return { isExternal: false, path: defaultPath };
  }

  if (SITE_PATH.test(ctaUrl)) {
    return { isExternal: false, path: ctaUrl };
  }

  const url = new URL(ctaUrl);

  if (url.origin === window.location.origin) {
    return {
      isExternal: false,
      path: `${url.pathname}${url.search}${url.hash}`,
    };
  }

  return { isExternal: true, url: url.href };
};
